<template>
    <div class="mqtt-operations">
        <div class="operations-header">
            <h3>MQTT操作</h3>
            <el-tag v-if="selectedBox" type="info">
                当前盒子: {{ selectedBox.name }}
            </el-tag>
            <el-tag v-else type="warning">
                请先选择盒子
            </el-tag>
        </div>

        <div class="operations-content">
            <el-tabs v-model="activeTab" type="border-card">
                <!-- 测量命令标签页 -->
                <el-tab-pane label="测量命令" name="measure">
                    <div class="operation-form">
                        <el-form :model="measureForm" label-width="120px">
                            <el-form-item label="操作类型">
                                <el-select v-model="measureForm.operation" placeholder="选择操作类型">
                                    <el-option
                                        v-for="op in measureOperations"
                                        :key="op.value"
                                        :label="op.label"
                                        :value="op.value" />
                                </el-select>
                            </el-form-item>

                            <el-form-item label="端口号">
                                <el-input-number
                                    v-model="measureForm.port"
                                    :min="1"
                                    :max="6"
                                    placeholder="1-6" />
                            </el-form-item>

                            <el-form-item label="设备ID">
                                <el-input-number
                                    v-model="measureForm.device_id"
                                    :min="1"
                                    :max="255"
                                    placeholder="1-255" />
                            </el-form-item>

                            <el-form-item label="消息ID">
                                <el-input
                                    v-model="measureForm.message_id"
                                    placeholder="可选，留空自动生成" />
                            </el-form-item>

                            <el-form-item>
                                <el-button
                                    type="primary"
                                    :disabled="!selectedBox || sendingMeasure"
                                    :loading="sendingMeasure"
                                    @click="sendMeasureCommand">
                                    发送测量命令
                                </el-button>
                            </el-form-item>
                        </el-form>
                    </div>
                </el-tab-pane>

                <!-- 查询状态标签页 -->
                <el-tab-pane label="查询状态" name="query">
                    <div class="operation-form">
                        <el-form :model="queryForm" label-width="120px">
                            <el-form-item label="查询类型">
                                <el-select v-model="queryForm.query_type" placeholder="选择查询类型">
                                    <el-option
                                        v-for="qt in queryTypes"
                                        :key="qt.value"
                                        :label="qt.label"
                                        :value="qt.value" />
                                </el-select>
                            </el-form-item>

                            <el-form-item label="串口序号">
                                <el-input-number
                                    v-model="queryForm.port"
                                    :min="1"
                                    :max="6"
                                    placeholder="可选，留空查询所有端口" />
                            </el-form-item>

                            <el-form-item label="设备ID">
                                <el-input-number
                                    v-model="queryForm.device_id"
                                    :min="1"
                                    :max="255"
                                    placeholder="1-255" />
                            </el-form-item>

                            <el-form-item label="消息ID">
                                <el-input
                                    v-model="queryForm.message_id"
                                    placeholder="可选，留空自动生成" />
                            </el-form-item>

                            <el-form-item>
                                <el-button
                                    type="primary"
                                    :disabled="!selectedBox || sendingQuery"
                                    :loading="sendingQuery"
                                    @click="sendQueryCommand">
                                    发送查询命令
                                </el-button>
                            </el-form-item>
                        </el-form>
                    </div>
                </el-tab-pane>
            </el-tabs>
        </div>
    </div>
</template>

<script>
import {ref, reactive, watch, getCurrentInstance} from 'vue';
import {notify} from '../../assets/js/utils.js';

export default {
    name: 'MqttOperations',
    props: {
        selectedBox: {
            type: Object,
            default: null,
        },
    },
    emits: ['command-sent'],
    setup(props, {emit}) {
        const activeTab = ref('measure');
        const sendingMeasure = ref(false);
        const sendingQuery = ref(false);

        // 获取当前组件实例，用于访问 axios
        const {proxy} = getCurrentInstance();

        // 测量操作选项
        const measureOperations = [
            {label: '获取电压', value: 'getVoltage'},
            {label: '获取温度', value: 'getTemp'},
            {label: '获取内阻', value: 'getResi'},
            {label: '计算内阻', value: 'calcResistance'},
            {label: '获取所有数据', value: 'getAll'},
        ];

        // 查询类型选项
        const queryTypes = [
            {label: '设备状态', value: 'status'},
            {label: '设备配置', value: 'config'},
            {label: '设备信息', value: 'info'},
        ];

        // 表单数据
        const measureForm = reactive({
            operation: 'getVoltage',
            port: 1,
            device_id: 1,
            message_id: '',
        });

        const queryForm = reactive({
            query_type: 'status',
            port: 1,
            device_id: 1,
            message_id: '',
        });

        // 发送测量命令
        const sendMeasureCommand = async () => {
            if (!props.selectedBox) return;

            sendingMeasure.value = true;
            try {
                const response = await proxy.axios.post('/api/mqtt/measure', {
                    box_id: props.selectedBox.id,
                    operation: measureForm.operation,
                    port: measureForm.port,
                    device_id: measureForm.device_id,
                    message_id: measureForm.message_id || undefined,
                });

                // 成功响应格式: {"data": {...}}
                notify('success', '测量命令发送成功');
                emit('command-sent', {
                    type: 'measure',
                    data: response.data.data,
                });
            } catch (error) {
                console.error('Failed to send measure command:', error);
                const errorMsg = error.response?.data?.message
                    || error.response?.data?.error
                    || error.message;
                notify('error', '发送测量命令失败: ' + errorMsg);
            } finally {
                sendingMeasure.value = false;
            }
        };

        // 发送查询命令
        const sendQueryCommand = async () => {
            if (!props.selectedBox) return;

            sendingQuery.value = true;
            try {
                const response = await proxy.axios.post('/api/mqtt/query', {
                    box_id: props.selectedBox.id,
                    query_type: queryForm.query_type,
                    port: queryForm.port || undefined,
                    device_id: queryForm.device_id,
                    message_id: queryForm.message_id || undefined,
                });

                // 成功响应格式: {"data": {...}}
                notify('success', '查询命令发送成功');
                emit('command-sent', {
                    type: 'query',
                    data: response.data.data,
                });
            } catch (error) {
                console.error('Failed to send query command:', error);
                const errorMsg = error.response?.data?.message
                    || error.response?.data?.error
                    || error.message;
                notify('error', '发送查询命令失败: ' + errorMsg);
            } finally {
                sendingQuery.value = false;
            }
        };

        // 重置表单当盒子改变时
        watch(() => props.selectedBox, (newBox) => {
            if (newBox) {
                // 可以根据盒子类型设置默认值
                console.log('Box selected:', newBox);
            }
        });

        return {
            activeTab,
            sendingMeasure,
            sendingQuery,
            measureOperations,
            queryTypes,
            measureForm,
            queryForm,
            sendMeasureCommand,
            sendQueryCommand,
        };
    },
};
</script>

<style scoped>
.mqtt-operations {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.operations-header {
  padding: 16px;
  border-bottom: 1px solid #dcdfe6;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.operations-header h3 {
  margin: 0;
  font-size: 16px;
  color: #303133;
}

.operations-content {
  flex: 1;
  padding: 16px;
}

.operation-form {
  max-width: 500px;
}

.el-form-item {
  margin-bottom: 18px;
}
</style>
